import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ComicPanel, ComicButton, ComicHeading } from '../components/ComicPanel';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('If that email exists, we sent a reset link.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Layout hideFooter>
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
          <ComicPanel className="w-full max-w-md p-8 text-center">
            <ComicHeading as="h1" className="text-3xl mb-6">
              Check Your Email
            </ComicHeading>
            <p className="text-slate-300 mb-8">
              If an account exists for {email}, you'll receive a password reset link shortly.
            </p>
            <ComicButton variant="ghost" onClick={() => setSent(false)}>
              Try another email
            </ComicButton>
          </ComicPanel>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
        <ComicPanel className="w-full max-w-md p-8">
          <ComicHeading as="h1" className="text-3xl text-center mb-8" data-testid="forgot-password-title">
            Forgot Password
          </ComicHeading>

          <p className="text-slate-400 text-center mb-6">
            Enter your email and we'll send you a reset link.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-yellow-500 rounded-none"
                placeholder="you@example.com"
                data-testid="forgot-email"
              />
            </div>

            <ComicButton 
              type="submit" 
              variant="primary" 
              className="w-full" 
              disabled={loading}
              data-testid="forgot-submit"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </ComicButton>
          </form>
        </ComicPanel>
      </div>
    </Layout>
  );
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        newPassword: password
      });
      toast.success('Password reset successfully!');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Reset failed. Link may be expired.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Layout hideFooter>
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
          <ComicPanel className="w-full max-w-md p-8 text-center">
            <ComicHeading as="h1" className="text-3xl mb-6 text-red-500">
              Invalid Link
            </ComicHeading>
            <p className="text-slate-300 mb-8">
              This password reset link is invalid or has expired.
            </p>
            <ComicButton variant="primary" onClick={() => navigate('/forgot-password')}>
              Request New Link
            </ComicButton>
          </ComicPanel>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-900">
        <ComicPanel className="w-full max-w-md p-8">
          <ComicHeading as="h1" className="text-3xl text-center mb-8" data-testid="reset-password-title">
            Reset Password
          </ComicHeading>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                New Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-yellow-500 rounded-none"
                placeholder="••••••••"
                data-testid="reset-password"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-yellow-500 uppercase font-bold tracking-widest text-sm">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-yellow-500 rounded-none"
                placeholder="••••••••"
                data-testid="reset-confirm-password"
              />
            </div>

            <ComicButton 
              type="submit" 
              variant="primary" 
              className="w-full" 
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </ComicButton>
          </form>
        </ComicPanel>
      </div>
    </Layout>
  );
};

export { ForgotPassword, ResetPassword };
